//
// # Pin Context Menu
//
// Displays pin information and actions when right-clicking on schematic pins.
// Currently supports renaming pins.
//

import type { Editor } from "./editor";
import type { SchPin } from "DrawCore/src/content/schematic/instance";
import { ContentKind } from "DrawCore/src/content/kind";
import { Palette } from "DrawCore/src/style";
import { BaseContextMenu, escapeHtml } from "./baseContextMenu";
import type { ContextMenuStyleConfig } from "./baseContextMenu";

export class PinContextMenu extends BaseContextMenu {
  // The pin under the mouse pointer
  private currentPin: SchPin | null = null;

  constructor(editor: Editor) {
    super(editor);
  }

  // -- Style config --

  readonly styleConfig: ContextMenuStyleConfig = {
    minWidth: "150px",
    maxWidth: "300px",
  };

  // -- Subclass stylesheet --

  protected getSubclassStyleSheet(): string {
    const p = Palette.getAll();
    return `
      .context-menu-header {
        font-weight: bold;
        margin-bottom: 4px;
        color: ${p.textTarget};
      }
      .context-menu-detail {
        color: ${p.textMuted};
        font-size: 11px;
      }
    `;
  }

  // -- clearState --

  protected clearState(): void {
    this.currentPin = null;
  }

  // -- Show --

  // Show context menu for a pin at the given position.
  show = (pin: SchPin, pageX: number, pageY: number): void => {
    // Close any other open context menus first
    this.editor.hideAllContextMenus();

    this.currentPin = pin;

    // Create or update menu element
    this.ensureMenuElement();

    // Build menu content
    const content = this.buildMenuContent(pin);

    this.displayMenu(content, pageX, pageY);

    // Set up click handlers for menu items
    this.setupMenuItemHandlers();
  };

  // Build the HTML content for the menu
  private buildMenuContent = (pin: SchPin): string => {
    const data = pin.data;
    const name = data.portName;
    const direction = data.direction;

    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header">Pin: ${escapeHtml(
      name
    )}</div>`;
    content += `<div class="context-menu-detail">Direction: ${direction}</div>`;
    content += `</div>`;

    // Add separator
    content += `<div class="context-menu-separator"></div>`;

    // Add action items
    content += `<div class="context-menu-actions">`;
    content += `<div class="context-menu-item" data-action="rename">Rename...</div>`;
    content += `<div class="context-menu-item" data-action="edit-properties">Edit Parameters...</div>`;
    content += `<div class="context-menu-item" data-action="delete">Delete</div>`;
    content += `</div>`;

    return content;
  };

  // Set up click handlers for menu items
  private setupMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const menuItems = this.menuElement.querySelectorAll(".context-menu-item");
    menuItems.forEach((item) => {
      const element = item as HTMLElement;
      element.onclick = (e) => {
        e.stopPropagation();
        const action = element.getAttribute("data-action");

        if (action === "rename") {
          this.handleRename();
        } else if (action === "edit-properties") {
          this.handleEditProperties();
        } else if (action === "delete") {
          this.handleDelete();
        }

        this.hide();
      };
    });
  };

  // Handle rename action - notify platform to show rename dialog
  private handleRename = (): void => {
    const { editor, currentPin } = this;
    if (!currentPin) return;

    const pinId = currentPin.data.id;
    const pinName = currentPin.data.portName;
    // Use == null to check both null and undefined; 0 is a valid pin ID
    if (pinId == null || !pinName) {
      editor.platform.warn("[PinContextMenu] Cannot rename pin without ID");
      return;
    }

    // Notify platform to open the rename dialog for this pin
    editor.platform.notifyRenamePin(pinId, pinName);
  };

  // Handle edit-properties action - notify platform to open the pin dialog
  // in editProperties mode for this pin.
  private handleEditProperties = (): void => {
    const { editor, currentPin } = this;
    if (!currentPin) return;

    const pinId = currentPin.data.id;
    const pinDir = currentPin.data.direction;
    // pin.data.id is `number | undefined`; 0 is a valid pin ID so we check for undefined explicitly.
    if (pinId === undefined) {
      editor.platform.warn(
        "[PinContextMenu] Cannot edit properties of pin without ID"
      );
      return;
    }

    editor.platform.notifyEditPinProperties(pinId, pinDir);
  };

  // Handle delete action
  private handleDelete = (): void => {
    const { editor, currentPin } = this;
    if (!currentPin) return;

    // Delete the pin through the schematic's delete mechanism
    if (editor.content.contentKind === ContentKind.Schematic) {
      // Explicitly select the pin before deleting to ensure we delete the right one
      editor.content.select(currentPin);
      editor.content.deleteSelected();
    }
  };
}
